import { useState } from "react"
import { useParams } from "react-router"
import type { PlayerActionType } from "@mc/types"
import { sendPlayerAction } from "../api/players.js"
import { friendlyError } from "../api/errorMessages.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { useToast } from "../components/ui/Toast.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"

const ACTIONS: { action: PlayerActionType; label: string; destructive?: boolean }[] = [
  { action: "kick", label: "Expulsar", destructive: true },
  { action: "ban", label: "Banear", destructive: true },
  { action: "unban", label: "Quitar ban" },
  { action: "op", label: "Dar OP" },
  { action: "deop", label: "Quitar OP" },
  { action: "whitelist_add", label: "Agregar a whitelist" },
  { action: "whitelist_remove", label: "Quitar de whitelist" },
]

/**
 * `/servers/[id]/players` (FR seccion 19 item 12). Solo acciones, sin
 * roster de jugadores en linea: ver el doc comment de
 * routes/players.ts para el motivo (el stats endpoint de Crafty que
 * daria esa lista sigue sin verificar).
 */
export function ServerPlayersPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [player, setPlayer] = useState("")
  const [pendingAction, setPendingAction] = useState<PlayerActionType | null>(null)
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("server-players")

  async function runAction(action: PlayerActionType) {
    if (!player.trim() || !serverId) return
    setPendingAction(action)
    try {
      await sendPlayerAction(serverId, action, player.trim())
      toast.show(`Acción "${ACTIONS.find((a) => a.action === action)?.label}" enviada.`, "success")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setPendingAction(null)
    }
  }

  function handleClick(action: PlayerActionType, destructive?: boolean) {
    if (!destructive) {
      runAction(action)
      return
    }
    toast.confirmDestructive(
      `Se va a ${action === "kick" ? "expulsar" : "banear"} a "${player.trim()}".`,
      () => runAction(action),
      () => toast.show("Cancelado.", "info"),
    )
  }

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", maxWidth: 480 }}>
      <Input label="Nombre de jugador" value={player} onChange={(e) => setPlayer(e.target.value)} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
        {ACTIONS.map(({ action, label, destructive }) => (
          <Button
            key={action}
            size="sm"
            variant={destructive ? "destructive" : "secondary"}
            loading={pendingAction === action}
            disabled={!player.trim() || pendingAction !== null}
            onClick={() => handleClick(action, destructive)}
          >
            {label}
          </Button>
        ))}
      </div>
    </div>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Jugadores</h1>
        <p style={{ color: "var(--text-muted)" }}>Escribí un nombre de jugador y elegí una acción.</p>
      </div>
      {isFirstVisit ? (
        <Coachmark title={getHint("server-players").title} description={getHint("server-players").description} onDismiss={dismissFirstVisit}>
          {content}
        </Coachmark>
      ) : (
        content
      )}
    </div>
  )
}
